/** @format */

import { Chain, CheckoutPayment, Payment } from '@shared/data-access/interfaces';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { CheckoutPaymentPricing, IGift } from '@cart-checkout/data-access/interfaces';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'shared-components-payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrl: './payment-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentSummaryComponent implements OnInit {
  @Input()
  public gifting: IGift;

  @Input()
  public addresses;

  @Input()
  public proxyPickup;

  @Input()
  public orderNumber;

  @Input()
  public confirmationPage = false;

  @Input()
  public orderPayments: Payment[] = [];

  @Input()
  public contact;

  @Input()
  public checkoutPayment: CheckoutPayment;

  @Input()
  public checkoutPricing: CheckoutPaymentPricing;

  @Input()
  public displayNameInBillingAddress = false;

  @Input()
  isPaypalReviewPage = false;

  public gift;
  public additional;
  public address;
  public payments = [];
  public paymentType;

  protected readonly platformId = inject(PLATFORM_ID);
  private readonly chain = inject(Chain);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.confirmationPage) {
        this.gift = { email: this.gifting?.recipient, message: this.gifting?.message };
        this.additional = {
          firstName: this.proxyPickup?.first_name,
          lastName: this.proxyPickup?.last_name,
          email: this.proxyPickup?.email,
        };
        if (this.addresses?.BILLING) {
          this.address = this.addresses?.BILLING;
          this.contact = {
            firstName: this.addresses?.BILLING?.first_name,
            lastName: this.addresses?.BILLING?.last_name,
            email: this.addresses?.BILLING?.email,
            phone: this.addresses?.BILLING?.phone,
          };
        } else if (this.addresses?.SHIPPINGBILLING) {
          this.address = this.addresses?.SHIPPINGBILLING;
          this.contact = {
            firstName: this.addresses?.SHIPPINGBILLING?.first_name,
            lastName: this.addresses?.SHIPPINGBILLING?.last_name,
            email: this.addresses?.SHIPPINGBILLING?.email,
            phone: this.addresses?.SHIPPINGBILLING?.phone,
          };
        }
        this.addPaymentsFromCheckout();
      } else {
        this.address = this.addresses;
        if (!this.contact) {
          this.contact = {
            firstName: this.addresses?.firstName.toLowerCase(),
            lastName: this.addresses?.lastName.toLowerCase(),
            email: this.addresses?.email.toLowerCase(),
            phone: this.addresses?.phone,
          };
        }
        this.additional = {
          firstName: this.proxyPickup?.firstName,
          lastName: this.proxyPickup?.lastName,
          email: this.proxyPickup?.email,
        };
        for (const orderPayment of this.orderPayments) {
          this.payments.push({
            amount: orderPayment.amount,
            title: this.generatePaymentTitle(orderPayment),
            icon: this.findIcon(orderPayment.paymentType),
            type: orderPayment.paymentType,
          });
        }
      }
    }
  }

  public findIcon(paymentType: string): string | undefined {
    const paymentIcons: { [key: string]: string } = {
      VISA: 'money-payments-visa',
      MASTERCARD: 'money-payments-mastercard',
      AMERICANEXPRESS: 'money-payments-amex',
      DISCOVER: 'money-payments-discover',

      'SCORE REWARDS CREDIT CARD': 'money-payments-scorecard-fill',

      'SCORE REWARDS MASTERCARD': 'money-payments-plcc-fill',
      PAYPAL: 'money-payments-paypal-fill',
      AFFIRM: 'money-payments-affirm-fill',
      CASH: 'money-cash-fill',
      AFTERPAY: 'money-payments-afterpay-fill',
    };

    const paymentTypeUpper = paymentType.toUpperCase();
    return paymentIcons[paymentTypeUpper] ?? undefined;
  }

  public generatePaymentTitle(payment?) {
    if (payment) {
      const number = payment.number || '';
      const type = payment.paymentType || '';
      const lastFour = number.length > 4 ? number.substring(number.length - 4) : number;
      let cardNumber = type ? `${type} ending in ${lastFour}` : number;
      if (type.toUpperCase() === 'CASH' || lastFour.length === 0) {
        cardNumber = type;
      }
      return cardNumber;
    }
    return '';
  }

  public get giftIcon(): string {
    return `${DCSG_CONSTANTS.baseImagePath}${this.chain.chainIdentifierAbbr}GiftIcon.svg`;
  }

  private addPaymentsFromCheckout() {
    // Add the credit card or resiliency to payments list
    if (
      this.checkoutPayment &&
      this.checkoutPayment.payment_processor &&
      this.checkoutPayment.payment_processor.payment_type &&
      this.checkoutPricing
    ) {
      const isCreditCard = !this.checkoutPayment.resiliency;
      if (isCreditCard) {
        this.payments.push({
          title: this.checkoutPayment.payment_processor.payment_type,
          type: this.checkoutPayment.payment_processor.payment_type,
          icon: this.findIcon(this.checkoutPayment.payment_processor.payment_type),
          amount: this.checkoutPricing.total_remaining ?? this.checkoutPricing.total,
        });
      } else {
        this.payments.push({
          title: 'Resiliency',
          type: 'Resiliency',
          icon: this.findIcon(''), // default icon
          amount: 0,
        });
      }
    }

    // Add any gift cards
    if (this.checkoutPayment && this.checkoutPayment.gift_cards) {
      const giftCards: any[] = this.checkoutPayment.gift_cards;
      for (const giftCard of giftCards) {
        this.payments.push({
          amount: giftCard.amount,
          title: `Gift Card ending in ${giftCard.card_number.replace(/\*/g, '')}`,
          type: 'Gift Card',
        });
      }
    }
  }

  trackByFn(index: number, _item: any): number {
    return index;
  }
}
